import {Button, Grid} from "@mui/material";
import "dayjs/locale/it";
import React, {RefObject, useContext, useEffect, useState} from "react";
import {useFormContext} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useRecoilState, useRecoilValue} from "recoil";
import {
    FormGreenDiscountModel,
    GreenDiscountEntityRequest,
    GreenDiscountEntityResponse,
} from "../../../api/types/greenDiscount";
import {SelectElementModelBE, SelectElementModelFE} from "../../../api/types/generic";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {useCommercialCategoryRepository} from "../../../api/repositories/CommercialCategoryRepository";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {greenDiscountCreationAtom} from "../../../state/greenDiscount";
import {CommercialCategorySelectFE} from "../../../api/types/commercialCategory";
import {fromGenericBeToGenericFe, fromSingleGenericBeToSingleGenericFe,} from "../../../utils";
import dayjs from "dayjs";
import {CommercialSegmentEntityResponse} from "../../../api/types/commercialSegment";
import {useGreenDiscountRepository} from "../../../api/repositories/GreenDiscountRepository";
import {SET_SNACKBAR} from "../../../constants";
import CustomBanner from "../../../common/components/CustomBanner";
import {FormAutocomplete, FormDateTimePicker, FormTextField,} from "../../../common/form-fields";
import {useGreenDiscountDetailRepository} from "../../../api/repositories/GreenDiscountDetailRepository";
import DealerAndTenant from "../../../common/components/DealerAndTenant";

type UpsellingRateBaseDefinitionProps = {
    submitRef: RefObject<HTMLButtonElement>;
    refresh: Function;
};

const UpsellingRateBaseDefinition = ({submitRef, refresh,}: UpsellingRateBaseDefinitionProps) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {
        dispatchGlobalData,
        globalData: {user},
    } = useContext(GlobalContext);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    // Use Form Context
    const {register, setValue, handleSubmit, watch, getValues} = useFormContext<FormGreenDiscountModel>();

    // Use Repository Hooks
    const {putGreenDiscount, postGreenDiscount} = useGreenDiscountRepository();
    const {getGreenDiscountMatrix} = useGreenDiscountDetailRepository();

    // Use Repository Hooks
    const {getCommercialCategoriesByCommercialSegmentId} =
        useCommercialCategoryRepository();

    // Use Recoil
    const commercialSegmentList = useRecoilValue(commercialSegmentListAtom);
    const [greenDiscountCreation, setGreenDiscountCreation] = useRecoilState(
        greenDiscountCreationAtom
    );
    const {readOnly, definitionData, inUse} = greenDiscountCreation;

    useEffect(() => {
        setValue("id", definitionData?.id as number)
        setValue("name", definitionData?.name as string)
        setValue("description", definitionData?.description as string)
        setValue("commercialSegment", definitionData?.commercialSegment ? definitionData?.commercialSegment as SelectElementModelFE : null)
        setValue("commercialCategory", definitionData?.commercialCategory ? definitionData?.commercialCategory as CommercialCategorySelectFE : null)
        setValue("reservationFrom", definitionData?.reservationFrom as string)
        setValue("reservationTo", definitionData?.reservationTo as string)
        setValue("pickupFrom", definitionData?.pickupFrom as string)
        setValue("pickupTo", definitionData?.pickupTo as string)
        setValue("dealerId", definitionData?.dealerId as number)
        setValue("used", definitionData?.used as boolean)
        setValue("dealer", {
            code: definitionData?.dealer?.code as string,
            label: t('translation:dealers.'.concat(definitionData?.dealer?.code as string))
        })
        setValue("legalEntity", {
            code: definitionData?.legalEntity?.code as string,
            label: t('translation:legalEntities.'.concat(definitionData?.legalEntity?.code as string))
        })
        getCategoryList(definitionData?.commercialSegment as SelectElementModelFE)
    }, [])

    // Use State
    const [commercialCategoryList, setCommercialCategoryList] = useState<SelectElementModelFE[]>([]);
    const [commercialCategoryListOriginal, setCommercialCategoryListOriginal] = useState<CommercialCategorySelectFE[]>([]);

    // Watch della legal entity per triggerare il filtraggio
    const watchedLegalEntity = watch("legalEntity");

    const getCategoryList = async (value: SelectElementModelFE) => {
        const commercialCategoriesData = await getCommercialCategoriesByCommercialSegmentId((value as SelectElementModelFE)?.code as string);
        const commercialCategoryList: CommercialCategorySelectFE[] = fromGenericBeToGenericFe(commercialCategoriesData);
        setCommercialCategoryListOriginal(commercialCategoryList);
    };

    // Effect per filtrare le categorie quando cambia la legal entity
    useEffect(() => {
        const selectedLegalEntity = (watchedLegalEntity?.code as string)
            ?? (user.legalEntities?.length === 1 ? user.legalEntities[0].name : null);
        
        if (!selectedLegalEntity || commercialCategoryListOriginal.length === 0) {
            setCommercialCategoryList([]);
            return;
        }

        // Espandi le categorie in base alla legal entity selezionata
        const expandedCategories = new Map<string, CommercialCategorySelectFE>();
        commercialCategoryListOriginal.forEach(category => {
            category.labels?.forEach(labelObj => {
                if (labelObj.country === selectedLegalEntity) {
                    const key = labelObj.label;
                    if (!expandedCategories.has(key)) {
                        expandedCategories.set(key, {
                            ...category,
                            label: labelObj.label
                        });
                    }
                }
            });
        });
        setCommercialCategoryList(Array.from(expandedCategories.values()));
    }, [watchedLegalEntity, commercialCategoryListOriginal, user.legalEntities]);

    const handleChangeCommercialSegment = async (value: SelectElementModelFE) => {
        setValue("commercialSegment", value);
        setValue("commercialCategory", null);
        
        if (value) {
            const commercialCategoriesData = await getCommercialCategoriesByCommercialSegmentId((value as SelectElementModelFE).code as string);
            const commercialCategoryList: CommercialCategorySelectFE[] = fromGenericBeToGenericFe(commercialCategoriesData);
            setCommercialCategoryListOriginal(commercialCategoryList);
        } else {
            setCommercialCategoryListOriginal([]);
        }
    };

    const getDefinitionData = (data: GreenDiscountEntityResponse) => ({
        ...data,
        commercialCategory: fromSingleGenericBeToSingleGenericFe(
            data.commercialCategory
        ) as CommercialCategorySelectFE,
        commercialSegment: fromSingleGenericBeToSingleGenericFe(
            data.commercialCategory.commercialSegment
        ) as SelectElementModelFE,
        dealer: {code: data.dealer, label: data.dealer},
        legalEntity: {code: data.legalEntity, label: data.legalEntity},
    });

    const confirmDefinition = async (values: FormGreenDiscountModel) => {
        const newGreenDiscountList: GreenDiscountEntityRequest = {
            name: values.name,
            description: values.description,
            dealer: dealerFilteredOptions.length === 1 ? dealerFilteredOptions[0] : values.dealer?.code as string,
            legalEntity: legalEntityOptions.length === 1 ? everyLegalEntitiesWithoutAll[0] : values.legalEntity?.code as string,
            commercialCategory: {
                id: values.commercialCategory?.code as number,
                name: values.commercialCategory?.label as string,
                active: values.commercialCategory?.active as boolean,
                commercialSegment: values.commercialCategory?.commercialSegment as CommercialSegmentEntityResponse,
            },
            reservationFrom: dayjs(values.reservationFrom).toISOString(),
            reservationTo: dayjs(values.reservationTo).toISOString(),
            pickupFrom: dayjs(values.pickupFrom).toISOString(),
            pickupTo: dayjs(values.pickupTo).toISOString(),
            dealerId: 1,
            used: inUse
        };

        try {
            const creationResponse = definitionData?.id
                ? await putGreenDiscount({
                    id: definitionData?.id,
                    ...JSON.parse(JSON.stringify(newGreenDiscountList)),
                })
                : await postGreenDiscount(newGreenDiscountList);

            const matrixData = await getGreenDiscountMatrix(creationResponse.id);

            setGreenDiscountCreation({
                ...greenDiscountCreation,
                greenDiscountId: creationResponse.id,
                matrixData,
                step: 1,
                completed: [true, false],
                definitionData: getDefinitionData(creationResponse),
            });
            refresh();
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
        } catch (e) {
        }
    };

    return (
        <Grid container spacing={2} alignItems="start">
            {inUse && <CustomBanner message={t("pricing:greenDiscount.banner")} severity={"warning"}/>}
            <Grid container item xs={12} md={12} lg={12} spacing={2} rowSpacing={3}>
                {/* NAME UPSELLINGRATE LIST */}
                <Grid item xs={12} md={3}>
                    <FormTextField
                        {...register("name")}
                        label={`${t("pricing:upsellingRate.form.labels.name")}*`}
                        readOnly={readOnly}
                        autoFocus
                    />
                </Grid>
                {/* DESCRIPTION EXCESS_GROUP LIST */}
                <Grid item xs={12} md={3}>
                    <FormTextField
                        {...register("description")}
                        label={`${t("pricing:upsellingRate.form.labels.description")}`}
                        readOnly={readOnly}
                    />
                </Grid>
                <DealerAndTenant
                    readOnly={readOnly}
                    md={3}
                    legalEntityName={"legalEntity"}
                    dealerName={"dealer"}
                    dealerIsVisible={true}
                    idForReadOnly={getValues("id")}
                />
                {/* COMMERCIAL SEGMENTS */}
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        {...register("commercialSegment")}
                        label={`${t("pricing:upsellingRate.form.labels.commercialSegment")}*`}
                        options={commercialSegmentList}
                        isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                        readOnly={readOnly}
                        handleChange={handleChangeCommercialSegment}
                    />
                </Grid>
                {/* COMMERCIAL CATEGORY */}
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        {...register("commercialCategory")}
                        label={`${t("pricing:upsellingRate.form.labels.commercialCategory")}*`}
                        options={commercialCategoryList}
                        isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                        readOnly={readOnly || !watch("commercialSegment")}
                    />
                </Grid>
                {/* RESERVATION FROM */}
                <Grid item xs={12} md={3}>
                    <FormDateTimePicker
                        {...register("reservationFrom")}
                        label={`${t("pricing:ancillary.form.labels.reservationFrom")}*`}
                        readOnly={readOnly || inUse}
                    />
                </Grid>
                {/* RESERVATION TO */}
                <Grid item xs={12} md={3}>
                    <FormDateTimePicker
                        {...register("reservationTo")}
                        label={`${t("pricing:ancillary.form.labels.reservationTo")}*`}
                        readOnly={readOnly}
                    />
                </Grid>
                {/* PICK UP FROM */}
                <Grid item xs={12} md={3}>
                    <FormDateTimePicker
                        {...register("pickupFrom")}
                        label={`${t("pricing:ancillary.form.labels.pickupDateFrom")}*`}
                        readOnly={readOnly}
                    />
                </Grid>
                {/* PICK UP TO */}
                <Grid item xs={12} md={3}>
                    <FormDateTimePicker
                        {...register("pickupTo")}
                        label={`${t("pricing:ancillary.form.labels.pickupDateTo")}*`}
                        readOnly={readOnly}
                    />
                </Grid>
            </Grid>
            <Button ref={submitRef} sx={{display: "none"}} onClick={handleSubmit(confirmDefinition)}/>
        </Grid>
    );
};

export default UpsellingRateBaseDefinition;
